import { ReactNode, useContext } from 'react';

import { TEXT_VARIANTS } from '@pukka/common/constants';
import { Item, MenuItemProps } from '@radix-ui/react-dropdown-menu';
import { twMerge } from 'tailwind-merge';

import { Icon, IconNames } from '../Icon';
import { Text } from '../Text';

import { MenuContext, SIZES } from './config';

interface MenuOptionValueProps extends MenuItemProps {
  readonly type?: 'value';
  readonly icon?: IconNames;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly children?: ReactNode;
  readonly onClick?: () => void;
}

interface MenuOptionGroupProps {
  readonly type: 'group';
  readonly children?: string;
  readonly className?: string;
}

type MenuOptionProps = MenuOptionGroupProps | MenuOptionValueProps;

function MenuOption(props: MenuOptionProps) {
  const { type } = props;
  const { size, disabled, icon } = useContext(MenuContext);
  const sizing = SIZES[size];

  if (type === 'group') {
    const { children, className } = props;

    return (
      <Text variant={sizing.variant} color="primary" weight="semibold" className={twMerge(sizing.option.group, className)}>
        {children}
      </Text>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disabled: _disabled, children, className, icon: _icon, type: _type, onClick, ...rest } = props;
  const __disabled = _disabled ?? disabled;
  const __icon = _icon ?? icon;

  return (
    <Item
      {...rest}
      disabled={__disabled}
      className={twMerge(
        'flex flex-row items-center',
        'rounded-md',
        'select-none outline-none',
        'text-secondary',
        'fill-primary',
        TEXT_VARIANTS[sizing.variant].size,
        sizing.option.content,
        !__disabled && 'hover:bg-light-blue-50 focus:bg-light-blue-50 group-radix-highlighted:bg-light-blue-50',
        'transition duration-300',
        __disabled && 'cursor-not-allowed fill-neutral-400 text-neutral-400',
        className,
      )}
      onClick={__disabled ? undefined : onClick}
    >
      {__icon ? <Icon icon={__icon} size={sizing.icon} /> : null}
      {children}
    </Item>
  );
}

export { MenuOption };
